import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { types as at, errorAction } from "@authzsvc/api-lib";
import GroupSearchModal from "../../common/GroupSearchModal";
import TooltipIcon from "../../common/TooltipIcon";
import {
  getGroupReaders,
  addGroupReader,
  deleteGroupReader,
} from "../../../actions/api";
import { Button, Card, List, Modal, Table, Typography } from "antd";
import { DeleteOutlined, ReadOutlined, PlusOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { showErrorNotification, showSuccessNotification } from "../../../util";

const GroupReaders = ({ canManageGroup }) => {
  const dispatch = useDispatch();
  const { id } = useParams();

  const [readers, setReaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [itemsToRemove, setItemsToRemove] = useState([]);

  const dataSource =
    readers?.map((reader) => ({
      key: reader.id,
      groupIdentifier: reader.groupIdentifier,
      displayName: reader.displayName,
      description: reader.description,
    })) || [];

  const hasSelections = selectedRows.length > 0;

  const rowSelection = {
    selectedRowKeys: selectedRows,
    onChange: (selectedRowKeys) => {
      setSelectedRows(selectedRowKeys);
    },
    getCheckboxProps: () => ({
      disabled: !canManageGroup,
    }),
  };

  const tableColumns = [
    {
      title: "Identifier",
      dataIndex: "groupIdentifier",
      key: "groupIdentifier",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Name",
      dataIndex: "displayName",
      key: "displayName",
      width: 180,
      ellipsis: true,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 220,
      ellipsis: true,
    },
  ];

  const getReaders = useCallback(async () => {
    setLoading(true);
    const result = await dispatch(getGroupReaders(id));
    const isErrorAction = result.type === errorAction(at.GET_GROUP_READERS);
    if (isErrorAction) {
      showErrorNotification(
        result.payload.title ?? "Something went wrong",
        result.payload.message ?? "Could not get readers, try again"
      );
    } else {
      setReaders(result.payload.data);
    }

    setLoading(false);
  }, [dispatch, id]);

  const handleAddReader = async (readerId) => {
    setLoading(true);
    const result = await dispatch(addGroupReader(id, readerId));
    const isErrorAction = result.type === errorAction(at.ADD_GROUP_READER);
    if (isErrorAction) {
      showErrorNotification(
        result.payload.title ?? "Something went wrong",
        result.payload.message ?? "Could not add reader, try again"
      );
    } else {
      getReaders();
      showSuccessNotification("Group reader added successfully!");
    }
    setLoading(false);
  };

  const handleDeleteReader = async (readerIds) => {
    setLoading(true);
    const deletePromises = readerIds.map((readerId) =>
      dispatch(deleteGroupReader(id, readerId))
    );

    const results = await Promise.all(deletePromises);

    const hasErrors = results.some(
      (result) => result.type === errorAction(at.DELETE_GROUP_READER)
    );

    if (hasErrors) {
      showErrorNotification(
        "Something went wrong",
        "Could not delete some readers, try again"
      );
    } else {
      getReaders();
      showSuccessNotification("Group readers removed successfully!");
    }
    setLoading(false);
  };

  const getRowKey = (reader) => {
    return reader?.key;
  };

  const handleRemove = () => {
    const filteredReaders = readers
      ?.filter((reader) => selectedRows.includes(reader.id))
      .map((reader) => reader.groupIdentifier);

    setItemsToRemove(filteredReaders);
    setIsRemoveModalOpen(true);
  };

  const handleRemoveModalOk = () => {
    const readerIds = readers
      ?.filter((reader) => selectedRows.includes(reader.id))
      .map((reader) => reader.groupIdentifier);

    handleDeleteReader(readerIds);
    setSelectedRows([]);
    setIsRemoveModalOpen(false);
  };

  const handleRemoveModalCancel = () => {
    setIsRemoveModalOpen(false);
  };

  useEffect(() => {
    getReaders();
  }, [getReaders]);

  return (
    <>
      <Card
        style={{ marginBottom: 24 }}
        title={
          <Typography.Title level={4} style={{ margin: 0 }}>
            <ReadOutlined
              style={{ marginRight: 8, color: "#bfbfbf" }}
              aria-hidden="true"
            />
            Group Readers
            <TooltipIcon
              size={16}
              title="Members of reader groups can read group properties, such as membership, regardless of the group privacy settings."
            />
          </Typography.Title>
        }
        extra={
          canManageGroup && (
            <GroupSearchModal
              title="Add Group Reader"
              buttonText="Add Reader"
              onSelect={(group) => handleAddReader(group.id)}
              icon={<PlusOutlined />}
            />
          )
        }
      >
        <Table
          rowKey={getRowKey}
          loading={loading}
          pagination={{
            showSizeChanger: true,
            hideOnSinglePage: false,
          }}
          columns={tableColumns}
          dataSource={dataSource}
          rowSelection={rowSelection}
          aria-label="Group readers table"
        />

        {dataSource.length > 0 && (
          <div style={{ marginTop: "-48px" }}>
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined aria-hidden="true" />}
              disabled={!hasSelections || !canManageGroup}
              onClick={handleRemove}
              aria-label="Remove selected readers"
            />
          </div>
        )}
      </Card>

      <Modal
        title={
          <>
            <DeleteOutlined style={{ marginRight: 8, color: "#ff4d4f" }} />
            Confirm Deletion
          </>
        }
        open={isRemoveModalOpen}
        centered
        onOk={handleRemoveModalOk}
        onCancel={handleRemoveModalCancel}
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to remove the following group readers?</p>
        <List
          dataSource={itemsToRemove}
          renderItem={(item) => <List.Item>{item}</List.Item>}
          bordered
          size="small"
        />
      </Modal>
    </>
  );
};

export default GroupReaders;
